import { onBeforeUnmount, ref, watch, type Ref } from 'vue';

/**
 * Live pixel size of a chart's plot box, so the SVG is drawn 1:1 — strokes,
 * dots and labels keep their size instead of stretching with a viewBox.
 * Until the first measurement (and without ResizeObserver, as in jsdom) it
 * reports `fallback`.
 */
export function useChartSize(
  el: Ref<HTMLElement | null | undefined>,
  fallback: { width: number; height: number }
) {
  const width = ref(fallback.width);
  const height = ref(fallback.height);
  let observer: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box || box.width <= 0 || box.height <= 0) return;
      width.value = box.width;
      height.value = box.height;
    });
  }
  // The plot is often behind a v-if on data, so it appears after mount.
  watch(
    el,
    (next, prev) => {
      if (!observer) return;
      if (prev) observer.unobserve(prev);
      if (next) observer.observe(next);
    },
    { immediate: true, flush: 'post' }
  );
  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });
  return { width, height };
}
